/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { t } from '@web-solutions/module-localization';
import Analytics from '@web-solutions/module-analytics';
import { useNav } from 'src/onboarding/hooks/nav';
import { useStore } from 'src/onboarding/store/store';
import { useFrontPhoto } from './useFrontPhoto';
import { stopSharedCamera } from './PhotoCapture';
import { runHairstyleAnalysis } from './hairstyleAnalysis';
import { ReviewCard } from './Reviews';
import styles from './ScanScreen.module.scss';

type Props = {
  page: string;
  ns: string;            // namespace: title, trust, confirm + steps[]
  stepKeys: string[];    // e.g. ['landmarks','symmetry','skin','score','almost']
  durationMs?: number;
  /** testimonial shown while the loader runs: { ns, id } into the reviews block */
  review?: { ns: string; id: string };
};

/** Analysis loader — scans the user's OWN selfie with sequential progress bars. */
export const ScanScreen: React.FC<Props> = ({ page, ns, stepKeys, durationMs = 7000, review }) => {
  const { onNext, currentIndex } = useNav();
  const front = useFrontPhoto();
  const setResult = useStore((s: any) => s.setHairstyleResult) as (r: any) => void;
  const shots = [front];

  const [elapsed, setElapsed] = useState(0);
  const [timerDone, setTimerDone] = useState(false);
  const [analysisDone, setAnalysisDone] = useState(false);
  const start = useRef(performance.now());

  // fire the real analysis (skin + symmetry) while the loader animates
  useEffect(() => {
    stopSharedCamera(); // selfies are done — release the camera
    Analytics.trackEvent(`step${currentIndex + 1}`, 'view', { page });
    runHairstyleAnalysis(front)
      .then((r) => { setResult(r); })
      .catch(() => {})
      .finally(() => setAnalysisDone(true));
    // safety: never hang if a request stalls — advance with whatever we have
    const safety = setTimeout(() => setAnalysisDone(true), durationMs + 8000);

    let raf = 0;
    const tick = (now: number) => {
      const e = Math.min((now - start.current) / durationMs, 1);
      setElapsed(e);
      if (e < 1) raf = requestAnimationFrame(tick);
      else setTimerDone(true);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); clearTimeout(safety); };
  }, []);

  // advance only when both the animation AND the real analysis have finished
  useEffect(() => {
    if (timerDone && analysisDone) {
      Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', { page });
      const id = setTimeout(() => onNext(), 350);
      return () => clearTimeout(id);
    }
  }, [timerDone, analysisDone]);

  const n = stepKeys.length;
  const stepPct = (i: number) => {
    const from = i / n;
    const span = 1 / n;
    const local = Math.min(1, Math.max(0, (elapsed - from) / span));
    if (i === n - 1) return Math.round(local * 24); // last bar never completes
    return Math.round(local * 100);
  };
  const activeIdx = Math.min(n - 1, Math.floor(elapsed * n));

  return (
    <div className={styles.wrap}>
      <p className={styles.trust}>🔒 {t(`${ns}.trust`)}</p>

      <div className={styles.stage}>
        {shots.map((src, i) => (
          <div key={i} className={styles.scanCard}>
            {src ? <img src={src} alt="" className={styles.scanImg} /> : <div className={styles.scanImg} />}
            <span className={styles.scanLine} />
            <span className={styles.scanGlow} aria-hidden="true" />
          </div>
        ))}
      </div>

      <p className={styles.confirm}>{t(`${ns}.confirm`)}</p>
      <h1 className={styles.title}>{t(`${ns}.title`)}</h1>

      <div className={styles.bars}>
        {stepKeys.map((key, i) => {
          const pct = stepPct(i);
          const done = pct >= 100;
          return (
            <div key={key} className={`${styles.barRow} ${i <= activeIdx ? styles.on : ''}`}>
              <div className={styles.barTop}>
                <span className={styles.barLabel}>{t(`${ns}.steps.${key}`)}</span>
                <span className={styles.barPct}>{done ? '100%' : `${pct}%`}</span>
              </div>
              <div className={styles.bar}><i style={{ width: `${pct}%` }} /></div>
            </div>
          );
        })}
      </div>

      {review && (
        <div className={styles.review}>
          <ReviewCard ns={review.ns} id={review.id} />
        </div>
      )}
    </div>
  );
};

export default ScanScreen;
